import { useQuery } from 'react-query';
import { getAuth } from 'firebase/auth';
import { firebaseApp } from '@/src/auth/FirebaseAuth';
export interface ICountry {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  phonecode: string;
  capital: string;
  currency: string;
  native: string;
  emoji: string;
  emojiU: string;
  latitude?: string;
  longitude?: string;
}

export interface IState {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  latitude?: string;
  longitude?: string;
}

export interface ICity {
  id: number;
  name: string;
  state_code: string;
  country_code: string;
  latitude: string;
  longitude: string;
}

type ApiResponse<T> = {
  status: 'SUCCESS' | 'FAILURE';
  data?: T;
  error?: string;
};

function getFunctionUrl(functionName: string): string {
  const functionsBaseUrl = import.meta.env.VITE_FUNCTIONS_BASE_URL;
  if (functionsBaseUrl) {
    return `${functionsBaseUrl}/${functionName}`;
  }
  
  const projectId = import.meta.env.VITE_PROJECT_ID;
  if (!projectId) {
    throw new Error('VITE_PROJECT_ID is not set');
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;
}

async function callFunction<T>(functionName: string, queryParams?: Record<string, string>): Promise<T> {
  const url = getFunctionUrl(functionName);
  const auth = getAuth(firebaseApp);
  const user = auth.currentUser;
  
  const headers: HeadersInit = { 'Accept': 'application/json' };
  
  if (user) {
    try {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch {
    }
  }
  
  const urlWithParams = new URL(url);
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      urlWithParams.searchParams.append(key, value);
    });
  }
  
  try {
    const response = await fetch(urlWithParams.toString(), {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data: ApiResponse<T> = await response.json();
    
    if (data.status === 'FAILURE') {
      throw new Error(data.error || 'Request failed');
    }
    
    if (typeof data.data === 'undefined') {
      throw new Error('No data in response');
    }
    
    return data.data;
  } catch (error: any) {
    throw error;
  }
}

export const countryStateCityApi = {
  getAllCountries: () => callFunction<ICountry[]>('locations-countries-getAllCountries'),
  
  getCountry: (countryCode: string) => 
    callFunction<ICountry | null>('locations-countries-getCountry', {
      countryCode: countryCode.toUpperCase(),
    }),
  
  getStatesOfCountry: (countryIsoCode: string) =>
    callFunction<IState[]>('locations-states-getStates', {
      countryCode: countryIsoCode.toUpperCase(),
    }),
  
  getState: (countryIsoCode: string, stateIsoCode: string) =>
    callFunction<IState | null>('locations-states-getState', {
      countryCode: countryIsoCode.toUpperCase(),
      stateCode: stateIsoCode.toUpperCase(),
    }),
  
  getCitiesOfState: (countryIsoCode: string, stateIsoCode: string) =>
    callFunction<ICity[]>('locations-cities-getCitiesByState', {
      countryCode: countryIsoCode.toUpperCase(),
      stateCode: stateIsoCode.toUpperCase(),
    }),
  
  getCitiesOfCountry: (countryIsoCode: string) =>
    callFunction<ICity[]>('locations-cities-getCitiesByCountry', {
      countryCode: countryIsoCode.toUpperCase(),
    }),
};

export function useCountries() {
  return useQuery(['countries'], countryStateCityApi.getAllCountries);
}

export function useStates(countryIsoCode: string | null | undefined) {
  return useQuery(
    ['states', countryIsoCode],
    () => countryStateCityApi.getStatesOfCountry(countryIsoCode!),
    { enabled: !!countryIsoCode }
  );
}

export function useCities(countryIsoCode: string | null | undefined, stateIsoCode: string | null | undefined) {
  return useQuery(
    ['cities', countryIsoCode, stateIsoCode],
    () => countryStateCityApi.getCitiesOfState(countryIsoCode!, stateIsoCode!),
    { enabled: !!countryIsoCode && !!stateIsoCode }
  );
}

export function useCitiesByCountry(
  countryIsoCode: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery(
    ['citiesByCountry', countryIsoCode],
    () => countryStateCityApi.getCitiesOfCountry(countryIsoCode!),
    { enabled: options?.enabled !== false && !!countryIsoCode }
  );
}
